/**
 * contractsMockService.js
 *
 * Servicio MOCK para el submódulo B2B · Contratos.
 *
 * ⚠️ TEMPORAL: todos los datos están hardcodeados hasta que existan los
 * endpoints reales. Cuando el backend exponga el SDK (p. ej.
 * `ContractsManagmentB2BService` del `@sdk-point/talisis`), este archivo se
 * reemplaza por las llamadas reales sin tocar las vistas: las páginas solo
 * dependen de la firma de estos métodos.
 *
 * Contrato que replica el flujo del módulo B2B existente:
 *  - getContracts({ companyTag, status }) → GET listado (la consulta). Igual que
 *    en Membresías, la EMPRESA es obligatoria y ancla el scope (internal/external).
 *  - getContractDetail(contractId)        → detalle de un contrato.
 *  - getContractUsage({ companyTag })      → consumo de licencias por empresa.
 *  - getContractLog(contractId)            → bitácora de modificaciones.
 *  - createContract(payload)               → POST nuevo contrato (la creación).
 *  - updateContract(contractId, payload)   → guardar cambios del detalle.
 *
 * Las respuestas imitan la forma `{ success, data }` que usan los servicios del
 * SDK en este proyecto para que el swap a endpoints reales sea directo.
 */

// Latencia simulada para ver los loaders/estados de carga como en producción.
const LATENCY_MS = 500;

const delay = (data, ms = LATENCY_MS) =>
  new Promise((resolve) => setTimeout(() => resolve(data), ms));

// ───────────────────────────────────────────────────────────────
// Catálogo de membresías (igual al desglose de la propuesta visual).
// `type` indica si la membresía es standard o b2b, usado en la vista de
// consumo "solo total".
// ───────────────────────────────────────────────────────────────
const MEMBERSHIP_CATALOG = [
  { name: 'Freemium', type: 'standard' },
  { name: 'Lite', type: 'standard' },
  { name: 'Professional', type: 'standard' },
  { name: 'Premium', type: 'standard' },
  { name: 'Lite Plus', type: 'b2b' },
  { name: 'Learner', type: 'b2b' },
  { name: 'Playlist', type: 'b2b' },
  { name: 'UNID Online Flex', type: 'standard' },
  { name: 'Coursera', type: 'b2b' },
  { name: 'Coursera + HH Online', type: 'b2b' },
  { name: 'HH Online Standard', type: 'b2b' },
  { name: 'Linkedin', type: 'b2b' },
  { name: 'Linkedin + Coursera', type: 'b2b' },
  { name: 'Linkedin + HH Online', type: 'b2b' },
  { name: 'Linkedin + Coursera + HH Online', type: 'b2b' },
];

// ───────────────────────────────────────────────────────────────
// Datos MOCK de contratos.
//
// `company_name` se usa para hacer match contra el scope del usuario
// (internal/external) tal como lo hace el módulo de Membresías: el filtro por
// empresa se compara por nombre normalizado. Cada empresa puede tener varios
// contratos (vigente + anteriores).
//
// Modos:
//  - 'detailed'   → contrato CON desglose por membresía.
//  - 'total_only' → contrato SOLO total (cualquier membresía descuenta del total).
// ───────────────────────────────────────────────────────────────
const MOCK_CONTRACTS = [
  {
    id: 'ctr-crehana-2026',
    company_name: 'Crehana',
    company_tag: 'crehana',
    start_date: '2026-01-01',
    end_date: '2026-12-31',
    status: 'Vigente',
    mode: 'detailed',
    purchased: 100,
    assigned: 65,
    available: 35,
    freemium: 6,
    lines: [
      { membership: 'Lite', purchased: 40, assigned: 35 },
      { membership: 'Linkedin', purchased: 40, assigned: 20 },
      { membership: 'Coursera', purchased: 20, assigned: 10 },
    ],
    log: [
      {
        date: '2026-10-20 10:15',
        user: 'Interno Talisis',
        change: 'Total general',
        previous: '80',
        next: '100',
      },
      {
        date: '2026-10-20 10:15',
        user: 'Interno Talisis',
        change: 'Fin de vigencia',
        previous: '30/06/2026',
        next: '31/12/2026',
      },
    ],
  },
  {
    id: 'ctr-crehana-2025',
    company_name: 'Crehana',
    company_tag: 'crehana',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
    status: 'Anterior',
    mode: 'total_only',
    purchased: 80,
    assigned: 80,
    available: 0,
    freemium: 0,
    lines: [],
    log: [],
  },
  {
    id: 'ctr-chedraui-2026',
    company_name: 'Chedraui',
    company_tag: 'chedraui',
    start_date: '2026-03-01',
    end_date: '2027-02-28',
    status: 'Vigente',
    mode: 'total_only',
    purchased: 50,
    assigned: 12,
    available: 38,
    freemium: 4,
    lines: [],
    // Desglose observado (aunque no se compró por tipo) para la vista de consumo.
    usageBreakdown: [
      { membership: 'Freemium', assigned: 4 },
      { membership: 'Lite', assigned: 5 },
      { membership: 'Professional', assigned: 3 },
      { membership: 'Premium', assigned: 2 },
      { membership: 'Coursera', assigned: 1 },
      { membership: 'Linkedin', assigned: 1 },
    ],
    log: [
      {
        date: '2026-03-01 09:00',
        user: 'Interno Talisis',
        change: 'Alta de contrato',
        previous: '—',
        next: 'Total 50, sin desglose',
      },
      {
        date: '2026-03-01 09:00',
        user: 'Interno Talisis',
        change: 'Vigencia',
        previous: '—',
        next: '01/03/2026 – 28/02/2027',
      },
    ],
  },
  {
    id: 'ctr-kinedu-2026',
    company_name: 'Kinedu',
    company_tag: 'kinedu',
    start_date: '2026-02-01',
    end_date: '2027-01-31',
    status: 'Vigente',
    mode: 'detailed',
    purchased: 30,
    assigned: 8,
    available: 22,
    freemium: 2,
    lines: [
      { membership: 'Lite', purchased: 15, assigned: 5 },
      { membership: 'Linkedin', purchased: 15, assigned: 3 },
    ],
    log: [
      {
        date: '2026-02-01 11:30',
        user: 'Interno Talisis',
        change: 'Alta de contrato',
        previous: '—',
        next: 'Total 30, con desglose',
      },
    ],
  },
];

const normalize = (value) => (value || '').toString().trim().toUpperCase();

/**
 * GET listado de contratos (la consulta).
 *
 * @param {Object} filters
 * @param {string} [filters.companyTag] - Nombre/tag de la empresa (OBLIGATORIO en
 *   la vista, igual que Membresías; sin empresa no se consulta).
 * @param {string} [filters.status]     - 'Vigente' | 'Anterior' (opcional).
 * @returns {Promise<{success:boolean, data:Array}>}
 */
export const getContracts = ({ companyTag, status } = {}) => {
  let data = [...MOCK_CONTRACTS];

  if (companyTag) {
    data = data.filter(
      (c) =>
        normalize(c.company_name) === normalize(companyTag) ||
        normalize(c.company_tag) === normalize(companyTag)
    );
  }

  if (status && status !== 'Seleccione' && status !== 'Todas') {
    data = data.filter((c) => normalize(c.status) === normalize(status));
  }

  return delay({ success: true, data });
};

/**
 * Detalle de un contrato por id.
 * @param {string} contractId
 * @returns {Promise<{success:boolean, data:Object|null}>}
 */
export const getContractDetail = (contractId) => {
  const data = MOCK_CONTRACTS.find((c) => c.id === contractId) || null;
  return delay({ success: !!data, data });
};

/**
 * Consumo de licencias de la empresa. Devuelve el contrato VIGENTE de la empresa
 * y su desglose de uso. Para contratos 'detailed' usa `lines`; para 'total_only'
 * usa `usageBreakdown`.
 *
 * @param {Object} params
 * @param {string} params.companyTag - Empresa a consultar (obligatorio).
 * @returns {Promise<{success:boolean, data:Object|null}>}
 */
export const getContractUsage = ({ companyTag } = {}) => {
  const contract = MOCK_CONTRACTS.find(
    (c) =>
      (normalize(c.company_name) === normalize(companyTag) ||
        normalize(c.company_tag) === normalize(companyTag)) &&
      c.status === 'Vigente'
  );

  if (!contract) return delay({ success: false, data: null });

  const usagePct = contract.purchased
    ? Math.round((contract.assigned / contract.purchased) * 100)
    : 0;

  let rows;
  if (contract.mode === 'detailed') {
    rows = contract.lines.map((line) => ({
      membership: line.membership,
      purchased: line.purchased,
      assigned: line.assigned,
      available: line.purchased - line.assigned,
    }));
    // Freemium no descuenta: se muestra aparte.
    rows.push({
      membership: 'Freemium',
      purchased: '—',
      assigned: contract.freemium,
      available: 'No descuenta',
    });
  } else {
    const breakdown = contract.usageBreakdown || [];
    rows = MEMBERSHIP_CATALOG.map((m) => {
      const found = breakdown.find((b) => b.membership === m.name);
      const isFreemium = m.name === 'Freemium';
      return {
        membership: m.name,
        type: m.type,
        purchased_by_type: isFreemium ? 'No aplica' : 'Del total general',
        assigned: isFreemium
          ? `${found ? found.assigned : 0} · no descuenta`
          : found
          ? found.assigned
          : 0,
      };
    });
  }

  return delay({
    success: true,
    data: {
      ...contract,
      usagePct,
      rows,
    },
  });
};

/**
 * Bitácora de modificaciones de un contrato.
 * @param {string} contractId
 * @returns {Promise<{success:boolean, data:Array}>}
 */
export const getContractLog = (contractId) => {
  const contract = MOCK_CONTRACTS.find((c) => c.id === contractId);
  return delay({ success: !!contract, data: contract ? contract.log : [] });
};

/**
 * POST nuevo contrato (la creación). MOCK: no persiste, solo valida y simula el
 * alta exitosa. Cuando esté el endpoint real, aquí se llama al SDK.
 *
 * @param {Object} payload
 * @param {string} payload.company    - Empresa.
 * @param {string} payload.startDate  - Inicio de vigencia (YYYY-MM-DD).
 * @param {string} payload.endDate    - Fin de vigencia (YYYY-MM-DD).
 * @param {number} payload.total      - Total general comprado.
 * @param {boolean} payload.withDetail- Si captura desglose por membresía.
 * @param {Array}  [payload.lines]    - Desglose [{ membership, purchased }].
 * @returns {Promise<{success:boolean, data:Object, message:string}>}
 */
export const createContract = (payload = {}) => {
  return delay({
    success: true,
    message: 'El contrato ha sido creado exitosamente.',
    data: {
      id: `ctr-mock-${Date.now()}`,
      company_name: payload.company,
      start_date: payload.startDate,
      end_date: payload.endDate,
      status: 'Vigente',
      mode: payload.withDetail ? 'detailed' : 'total_only',
      purchased: Number(payload.total) || 0,
      assigned: 0,
      available: Number(payload.total) || 0,
      freemium: 0,
      lines: payload.lines || [],
      log: [],
    },
  });
};

/**
 * Guardar cambios del detalle del contrato (total/vigencia/desglose). MOCK.
 * @param {string} contractId
 * @param {Object} payload
 * @returns {Promise<{success:boolean, message:string}>}
 */
export const updateContract = (contractId, payload = {}) => {
  return delay({
    success: true,
    message: 'Los cambios del contrato se guardaron correctamente.',
    data: { id: contractId, ...payload },
  });
};

export { MEMBERSHIP_CATALOG };
